import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

/**
 * Compact row of daily weather icons.
 * Modes:
 *  - Backend proxy (recommended): provide propertyId; component hits /weather-forecast/ endpoint.
 *  - Direct OpenWeather: provide lat, lon, apiKey to call OpenWeather from browser.
 */
export default function WeatherForecastIcons({ propertyId, lat, lon, apiKey, days = 7, units = 'imperial', style }) {
  const [data, setData] = useState(null);        // raw OpenWeather data when using direct mode
  const [proxyDays, setProxyDays] = useState([]); // normalized days from backend proxy
  const [proxyMeta, setProxyMeta] = useState({ timezone_offset: 0, units });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Backend proxy mode
  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${propertyId}/weather-forecast/`, {
      params: { units }
    }).then((resp) => {
      if (cancelled) return;
      const tzOffset = resp?.data?.timezone_offset ?? 0;
      const daysRaw = Array.isArray(resp?.data?.days) ? resp.data.days : [];
      // Prefer One Call daily payload when available
      const daysArr = daysRaw.slice(0, days).map(d => {
        const daily = d.daily || {};
        const temp = daily.temp || {};
        const feels = daily.feels_like || {};
        const popPct = typeof daily.pop_pct === 'number' ? daily.pop_pct
                        : (typeof d.max_pop === 'number' ? d.max_pop : null);
        const hiCandidate = typeof temp.max === 'number' ? temp.max
                          : (typeof d.hi === 'number' ? d.hi
                            : (typeof d.temp === 'number' ? d.temp : null));
        const loCandidate = typeof temp.min === 'number' ? temp.min
                          : (typeof d.lo === 'number' ? d.lo : null);
        return {
          date: d.date,
          weekday: d.day || null,
          desc: d.description || '',
          iconUrl: d.icon || '',
          hi: hiCandidate,
          lo: loCandidate,
          popPct: popPct,
          summary: d.summary || '',
          // Extra fields for tooltip
          details: {
            temp,
            feels,
            pressure: daily.pressure,
            humidity: daily.humidity,
            dew_point: daily.dew_point,
            wind_speed: daily.wind_speed,
            wind_gust: daily.wind_gust,
            wind_deg: daily.wind_deg,
            clouds: daily.clouds,
            uvi: daily.uvi,
            rain_in: daily.rain_in,
            snow_in: daily.snow_in,
            rain_mm: daily.rain_mm,
            snow_mm: daily.snow_mm,
            sunrise: daily.sunrise,
            sunset: daily.sunset,
            moonrise: daily.moonrise,
            moonset: daily.moonset,
            moon_phase: daily.moon_phase,
            weather: daily.weather,
          }
        };
      });
      setProxyDays(daysArr);
      setProxyMeta({ timezone_offset: tzOffset, units: resp?.data?.units || units });
    }).catch((e) => {
      if (!cancelled) setError(e?.message || 'Weather unavailable');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [propertyId, units, days]);

  // Direct OpenWeather mode (fallback only when propertyId not provided)
  useEffect(() => {
    if (propertyId) return; // do not run when using proxy mode
    if (!lat || !lon || !apiKey) return;
    const ctrl = new AbortController();
    const url = `https://api.openweathermap.org/data/2.5/forecast?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&appid=${encodeURIComponent(apiKey)}&units=${encodeURIComponent(units)}`;
    setLoading(true);
    setError(null);
    axios.get(url, { signal: ctrl.signal })
      .then((resp) => {
        setData(resp.data);
      })
      .catch((e) => {
        if (!axios.isCancel(e)) setError(e.message || 'Failed to load weather');
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [propertyId, lat, lon, apiKey, units]);

  const directDays = useMemo(() => {
    if (!data?.list?.length) return [];
    const weekdayFromYmd = (ymd) => {
      try {
        // Interpret the provided YYYY-MM-DD as the intended local day by anchoring at noon UTC,
        // then take the UTC weekday abbreviation to avoid local timezone shifts.
        const d = new Date(ymd + 'T12:00:00Z');
        const wk = d.toUTCString().slice(0, 3); // e.g., 'Sat'
        return wk;
      } catch { return null; }
    };
    // Group by date (YYYY-MM-DD from dt_txt)
    const byDate = new Map();
    for (const item of data.list) {
      const [date] = (item.dt_txt || '').split(' ');
      if (!date) continue;
      if (!byDate.has(date)) byDate.set(date, []);
      byDate.get(date).push(item);
    }
    const pickForDate = (arr) => {
      if (!arr || arr.length === 0) return null;
      const target = '12:00:00';
      let best = arr[0];
      let bestDelta = Infinity;
      for (const it of arr) {
        const t = (it.dt_txt || '').split(' ')[1] || '';
        if (t === target) return it;
        const hour = parseInt(t.slice(0, 2) || '0', 10);
        const delta = Math.abs(hour - 12);
        if (delta < bestDelta) { bestDelta = delta; best = it; }
      }
      return best;
    };
    const entries = [];
    for (const [date, arr] of byDate) {
      const chosen = pickForDate(arr);
      if (chosen) {
        let hi = -Infinity, lo = Infinity;
        let desc = chosen.weather?.[0]?.description || '';
        let icon = chosen.weather?.[0]?.icon || '';
        let maxPop = null;
        for (const it of arr) {
          const t = it?.main?.temp;
          if (typeof t === 'number') { if (t > hi) hi = t; if (t < lo) lo = t; }
          const pop = it?.pop;
          if (typeof pop === 'number') {
            const pct = Math.max(0, Math.min(1, pop)) * 100;
            maxPop = maxPop == null ? pct : Math.max(maxPop, pct);
          }
        }
        entries.push({
          date,
          weekday: weekdayFromYmd(date),
          desc,
          iconUrl: icon ? `https://openweathermap.org/img/wn/${icon}.png` : '',
          hi: isFinite(hi) ? hi : null,
          lo: isFinite(lo) ? lo : null,
          popPct: maxPop != null && isFinite(maxPop) ? Math.round(maxPop) : null,
        });
      }
    }
    entries.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    return entries.slice(0, Math.min(days, entries.length));
  }, [data, days]);

  const items = propertyId ? proxyDays : directDays;
  if (loading) return null; // keep UI compact; skip spinner in header
  if (error) return null;   // silently ignore in header
  if (!items.length) return null;

  const effUnits = propertyId ? (proxyMeta.units || units) : units;
  const unitSymbol = effUnits === 'metric' ? '°C' : effUnits === 'standard' ? 'K' : '°F';

  const mmToIn = (mm) => (typeof mm === 'number' ? Math.round((mm / 25.4) * 100) / 100 : null);
  const fmtInches = (val) => (typeof val === 'number' ? `${val.toFixed(2)}"` : null);
  const fmtTime = (epochSec) => {
    if (!epochSec) return null;
    try {
      const offset = (proxyMeta.timezone_offset || 0) * 1000; // ms
      const d = new Date((epochSec * 1000) + offset);
      return d.toUTCString().split(' ')[4].slice(0,5); // HH:MM
    } catch { return null; }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...(style || {}) }}>
      {items.map(({ date, weekday, desc, iconUrl, hi, lo, popPct, summary, details }) => {
        const short = weekday || (function(){
          try { return new Date(date + 'T12:00:00Z').toUTCString().slice(0,3); } catch { return ''; }
        })();
        const hiLo = (hi != null || lo != null)
          ? `${lo != null ? Math.round(lo) : '–'}${unitSymbol}/${hi != null ? Math.round(hi) : '–'}${unitSymbol}`
          : null;
        // Build rich tooltip (plain text title)
        const lines = [];
        lines.push(`${date}${summary ? ` — ${summary}` : ''}`);
        if (desc && !summary) lines.push(desc);
        if (hiLo) lines.push(`Lo/Hi: ${hiLo}`);
        if (typeof popPct === 'number') lines.push(`Precip chance: ${popPct}%`);
        if (details) {
          const t = details.temp || {};
          const fl = details.feels || {};
          const precipRainIn = typeof details.rain_in === 'number' ? details.rain_in : mmToIn(details.rain_mm);
          const precipSnowIn = typeof details.snow_in === 'number' ? details.snow_in : mmToIn(details.snow_mm);
          const temps = [];
          if (typeof t.morn === 'number') temps.push(`morn ${Math.round(t.morn)}${unitSymbol}`);
          if (typeof t.day === 'number') temps.push(`day ${Math.round(t.day)}${unitSymbol}`);
          if (typeof t.eve === 'number') temps.push(`eve ${Math.round(t.eve)}${unitSymbol}`);
          if (typeof t.night === 'number') temps.push(`night ${Math.round(t.night)}${unitSymbol}`);
          if (temps.length) lines.push(`Temps: ${temps.join(', ')}`);
          const feelsParts = [];
          if (typeof fl.day === 'number') feelsParts.push(`feels day ${Math.round(fl.day)}${unitSymbol}`);
          if (typeof fl.night === 'number') feelsParts.push(`night ${Math.round(fl.night)}${unitSymbol}`);
          if (feelsParts.length) lines.push(feelsParts.join(', '));
          if (typeof details.humidity === 'number') lines.push(`Humidity: ${details.humidity}%`);
          if (typeof details.dew_point === 'number') lines.push(`Dew: ${Math.round(details.dew_point)}${unitSymbol}`);
          const windParts = [];
          if (typeof details.wind_speed === 'number') windParts.push(`${Math.round(details.wind_speed)}${effUnits==='imperial'?' mph':' m/s'}`);
          if (typeof details.wind_gust === 'number') windParts.push(`gust ${Math.round(details.wind_gust)}${effUnits==='imperial'?' mph':' m/s'}`);
          if (windParts.length) lines.push(`Wind: ${windParts.join(', ')}`);
          if (typeof details.clouds === 'number') lines.push(`Clouds: ${details.clouds}%`);
          if (typeof details.uvi === 'number') lines.push(`UV: ${details.uvi}`);
          const precipParts = [];
          if (typeof precipRainIn === 'number' && precipRainIn > 0) precipParts.push(`rain ${fmtInches(precipRainIn)}`);
          if (typeof precipSnowIn === 'number' && precipSnowIn > 0) precipParts.push(`snow ${fmtInches(precipSnowIn)}`);
          if (precipParts.length) lines.push(`Precip: ${precipParts.join(', ')}`);
          const sr = fmtTime(details.sunrise);
          const ss = fmtTime(details.sunset);
          if (sr || ss) lines.push(`Sun: ${sr || '–'} / ${ss || '–'}`);
          if (typeof details.moon_phase === 'number') lines.push(`Moon phase: ${details.moon_phase}`);
        }
        const title = lines.join('\n');
        const displayPop = (typeof popPct === 'number' && isFinite(popPct)) ? Math.max(0, Math.round(popPct)) : 0;
        return (
          <div key={date} title={title} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#9ca3af', minWidth: 44 }}>
            {iconUrl && (<img alt={desc || 'weather'} src={iconUrl} style={{ width: 28, height: 28 }} />)}
            <span style={{ fontSize: 11, lineHeight: '12px', marginTop: 2 }}>{short}</span>
            {hiLo && (
              <span style={{ fontSize: 10, lineHeight: '11px', color: '#d1d5db', marginTop: 1 }}>{hiLo}</span>
            )}
            <span style={{ fontSize: 10, lineHeight: '11px', color: '#60a5fa', marginTop: 1 }}>{displayPop}%</span>
          </div>
        );
      })}
    </div>
  );
}
